import {
  Cartesian3,
  Color,
  JulianDate,
  VerticalOrigin,
  HorizontalOrigin,
  CallbackProperty,
  ReferenceFrame,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
} from "@cesium/engine";
import * as Astronomy from "astronomy-engine";
import { PlanetaryPositions } from "./PlanetaryPositions";
import { CelestialOrbitRenderer } from "./CelestialOrbitRenderer";

/**
 * Manages rendering of planets as celestial point sources in Cesium
 * Supports both billboards and point primitives
 */
export class PlanetManager {
  constructor(viewer) {
    this.viewer = viewer;
    this.planetary = new PlanetaryPositions();
    this.orbitRenderer = new CelestialOrbitRenderer(viewer);
    this.enabled = false;
    this.renderMode = "billboard"; // 'billboard' or 'point'
    this.planetEntities = [];
    this.pointPrimitives = null;
    this.updateInterval = null;
    this.preRenderListener = null;
    this.lastUpdateTime = null; // Last simulation time we updated point primitives
    this.lastRealUpdate = null; // Last real-world time we updated
    this.showLabels = true; // Whether to show planet labels
    this.showOrbits = false; // Whether to show planet orbits
    this.trackedEntityListener = null; // Listener to prevent planet tracking
    this.distanceCheckListener = null; // Listener for distance-based visibility (Uranus/Neptune)
    this.lastDistanceCheck = 0; // Last time we checked distance (in milliseconds)
    this.distanceCheckInterval = 1000; // Check distance every 1 second
    this.pointPickHandler = null; // Click handler for point primitive picking

    // Orbital periods for planets around the Sun (in seconds)
    // Source: NASA Solar System Dynamics
    this.orbitalPeriods = {
      Mercury: 87.969 * 24 * 60 * 60, // 87.969 days
      Venus: 224.701 * 24 * 60 * 60, // 224.701 days
      Mars: 686.98 * 24 * 60 * 60, // 686.980 days
      Jupiter: 4332.589 * 24 * 60 * 60, // 11.862 years
      Saturn: 10759.22 * 24 * 60 * 60, // 29.457 years
      Uranus: 30688.5 * 24 * 60 * 60, // 84.01 years
      Neptune: 60182.0 * 24 * 60 * 60, // 164.8 years
    };
  }

  /**
   * Enable planet rendering
   * @param {string} mode - 'billboard' or 'point'
   */
  async enable(mode = "billboard") {
    if (this.enabled) {
      this.disable();
    }

    this.enabled = true;
    this.renderMode = mode;

    if (mode === "billboard") {
      this.createBillboards();
      // Update metadata every 5 minutes for billboards
      this.updateInterval = setInterval(
        () => {
          this.updatePositions();
        },
        5 * 60 * 1000,
      );
    } else if (mode === "point") {
      await this.createPointPrimitives();
      // For point primitives, update on smooth scrubbing (every 0.5 seconds real time)
      this.preRenderListener = this.viewer.scene.preUpdate.addEventListener(() => {
        this.updatePointPrimitivesForScrubbing();
      });
    }

    // Listen for ClockMonitor time jump events to update planet positions
    this.handleClockTimeJump = (event) => {
      const { jumpSeconds } = event.detail;
      // Update positions on time jumps >1 hour
      if (Math.abs(jumpSeconds) > 3600) {
        this.updatePositions();
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("cesium:clockTimeJumped", this.handleClockTimeJump);
    }

    // Initial position update
    this.updatePositions();

    // Extend frustum far plane so distant planets aren't clipped
    this._updateFrustumFar();

    // Add listener to prevent planet tracking
    this.trackedEntityListener = this.viewer.trackedEntityChanged.addEventListener(() => {
      const tracked = this.viewer.trackedEntity;
      if (tracked && tracked.id && tracked.id.startsWith("planet")) {
        // Don't allow tracking planets - clear tracked entity
        this.viewer.trackedEntity = undefined;
      }
    });

    // Add listener for distance-based visibility (Uranus/Neptune fade-in)
    this.distanceCheckListener = this.viewer.scene.preRender.addEventListener(() => {
      this.updateDistanceVisibility();
    });

    // Add click handler for point primitive picking
    // PointPrimitives don't trigger Cesium's entity selection, so we handle it manually
    this.pointPickHandler = new ScreenSpaceEventHandler(this.viewer.scene.canvas);
    this.pointPickHandler.setInputAction((event) => {
      if (this.renderMode !== "point") return;
      const pickedObject = this.viewer.scene.pick(event.position);
      if (defined(pickedObject) && pickedObject.primitive === this.pointPrimitives) {
        // Find the label entity for this planet and select it
        const planetName = pickedObject.id;
        const labelEntity = this.planetEntities.find((e) => e.id === `planet-label-${planetName}`);
        if (labelEntity) {
          this.viewer.selectedEntity = labelEntity;
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /**
   * Disable planet rendering
   */
  disable() {
    if (!this.enabled) {
      return;
    }

    this.enabled = false;

    // Clear update interval
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // Clear preRender listener
    if (this.preRenderListener) {
      this.preRenderListener();
      this.preRenderListener = null;
    }

    // Clear tracked entity listener
    if (this.trackedEntityListener) {
      this.trackedEntityListener();
      this.trackedEntityListener = null;
    }

    // Clear distance check listener
    if (this.distanceCheckListener) {
      this.distanceCheckListener();
      this.distanceCheckListener = null;
    }

    // Remove ClockMonitor listener
    if (typeof window !== "undefined" && this.handleClockTimeJump) {
      window.removeEventListener("cesium:clockTimeJumped", this.handleClockTimeJump);
    }

    // Remove point pick handler
    if (this.pointPickHandler) {
      this.pointPickHandler.destroy();
      this.pointPickHandler = null;
    }

    // Clear all orbits
    this.orbitRenderer.clear();

    // Remove entities (billboards or labels)
    this.planetEntities.forEach((entity) => {
      this.viewer.entities.remove(entity);
    });
    this.planetEntities = [];

    // Remove point primitives
    if (this.renderMode === "point" && this.pointPrimitives) {
      this.viewer.scene.primitives.remove(this.pointPrimitives);
      this.pointPrimitives = null;
    }

    // Reset update tracking
    this.lastUpdateTime = null;
    this.lastRealUpdate = null;
  }

  /**
   * Switch between billboard and point rendering modes
   * @param {string} mode - 'billboard' or 'point'
   */
  async setRenderMode(mode) {
    if (this.renderMode === mode) {
      return;
    }

    const wasEnabled = this.enabled;
    if (wasEnabled) {
      // Save orbit state — disable() clears orbits but we want to preserve them
      const hadOrbits = this.showOrbits;
      this.disable();
      await this.enable(mode);
      // Restore orbit state
      if (hadOrbits) {
        this.showOrbits = true;
        this.updatePlanetOrbitsVisibility();
      }
    }
  }

  /**
   * Create billboard entities for planets
   */
  createBillboards() {
    const planets = this.planetary.getPlanetNames();

    planets.forEach((planetName) => {
      const planetData = this.planetary.planets.find((p) => p.name === planetName);

      // Create a callback property that calculates position at render time
      // Positions are transformed from ICRF to Fixed frame in PlanetaryPositions
      const positionCallback = new CallbackProperty((time, result) => {
        const positions = this.planetary.calculatePositions(time);
        const planet = positions.find((p) => p.name === planetName);
        if (planet) {
          return Cartesian3.clone(planet.position, result);
        }
        return result;
      }, false);

      // Create billboard entity
      const entity = this.viewer.entities.add({
        id: `planet-${planetName}`,
        name: `Planet: ${planetName}`,
        position: positionCallback,
        billboard: {
          image: this.createPlanetCanvas(planetData.color, 10),
          scale: 0.8,
          verticalOrigin: VerticalOrigin.CENTER,
          scaleByDistance: null, // Keep constant size
        },
        label: {
          text: planetData.symbol,
          font: "16px sans-serif",
          fillColor: Color.WHITE,
          outlineColor: Color.BLACK,
          outlineWidth: 2,
          style: 2, // FILL_AND_OUTLINE
          verticalOrigin: VerticalOrigin.TOP,
          horizontalOrigin: HorizontalOrigin.CENTER,
          pixelOffset: new Cartesian3(0, 8, 0),
          show: new CallbackProperty(() => this.showLabels, false),
        },
        description: new CallbackProperty((time) => {
          const positions = this.planetary.calculatePositions(time || this.viewer.clock.currentTime);
          const planet = positions.find((p) => p.name === planetName);
          return planet ? this.generateDescription(planet) : "";
        }, false),
      });

      this.planetEntities.push(entity);
    });
  }

  /**
   * Create point primitives for planets
   */
  async createPointPrimitives() {
    const { PointPrimitiveCollection } = await import("@cesium/engine");

    this.pointPrimitives = this.viewer.scene.primitives.add(new PointPrimitiveCollection());

    const planets = this.planetary.getPlanetNames();

    planets.forEach((planetName) => {
      const planetData = this.planetary.planets.find((p) => p.name === planetName);

      // Create point primitive
      const point = this.pointPrimitives.add({
        id: planetName,
        position: new Cartesian3(0, 0, 0), // Will be updated
        color: Color.fromBytes(planetData.color[0], planetData.color[1], planetData.color[2], 255),
        pixelSize: 5,
        outlineColor: Color.WHITE,
        outlineWidth: 1,
      });

      point.planetName = planetName; // Store planet name for updates

      // Create label entity for the point primitive
      // Labels must be entities, not primitives
      const positionCallback = new CallbackProperty((time, result) => {
        const positions = this.planetary.calculatePositions(time);
        const planet = positions.find((p) => p.name === planetName);
        if (planet) {
          return Cartesian3.clone(planet.position, result);
        }
        return result;
      }, false);

      const labelEntity = this.viewer.entities.add({
        id: `planet-label-${planetName}`,
        name: `Planet: ${planetName}`,
        position: positionCallback,
        label: {
          text: planetData.symbol,
          font: "16px sans-serif",
          fillColor: Color.WHITE,
          outlineColor: Color.BLACK,
          outlineWidth: 2,
          style: 2, // FILL_AND_OUTLINE
          verticalOrigin: VerticalOrigin.TOP,
          horizontalOrigin: HorizontalOrigin.CENTER,
          pixelOffset: new Cartesian3(0, 8, 0),
          show: new CallbackProperty(() => this.showLabels, false),
        },
        description: new CallbackProperty((time) => {
          const positions = this.planetary.calculatePositions(time || this.viewer.clock.currentTime);
          const planet = positions.find((p) => p.name === planetName);
          return planet ? this.generateDescription(planet) : "";
        }, false),
      });

      this.planetEntities.push(labelEntity); // Store for cleanup
    });
  }

  /**
   * Update point primitives during smooth scrubbing
   * Only updates if 0.5 seconds of real time passed (for continuous animation/scrubbing)
   * Time jumps are handled by ClockMonitor event listener
   */
  updatePointPrimitivesForScrubbing() {
    if (!this.enabled || this.renderMode !== "point") {
      return;
    }

    const now = Date.now();

    // Update if this is first time
    if (!this.lastRealUpdate) {
      this.lastRealUpdate = now;
      this.updatePositions();
      return;
    }

    // Update if 0.5 seconds of real time passed (for smooth scrubbing)
    if (now - this.lastRealUpdate > 500) {
      this.lastRealUpdate = now;
      this.updatePositions();
    }
  }

  /**
   * Update planet positions based on current simulation time
   */
  updatePositions() {
    if (!this.enabled) {
      return;
    }

    const currentTime = this.viewer.clock.currentTime;
    const positions = this.planetary.calculatePositions(currentTime);

    if (this.renderMode === "billboard") {
      // Billboards use CallbackProperty which auto-updates position and description
      // We only need to update scale based on magnitude
      positions.forEach((planetData) => {
        const entity = this.planetEntities.find((e) => e.id === `planet-${planetData.name}`);
        if (entity) {
          // Adjust size based on magnitude (brighter = larger)
          const scale = this.magnitudeToScale(planetData.magnitude);
          entity.billboard.scale = scale;
        }
      });
    } else if (this.renderMode === "point" && this.pointPrimitives) {
      positions.forEach((planetData) => {
        const point = this.pointPrimitives._pointPrimitives.find((p) => p.planetName === planetData.name);
        if (point) {
          point.position = planetData.position;

          // Adjust size based on magnitude (brighter = larger)
          const pixelSize = this.magnitudeToPixelSize(planetData.magnitude);
          point.pixelSize = pixelSize;
        }
      });
    }
  }

  /**
   * Convert magnitude to billboard scale
   * Brighter planets (more negative magnitude) get larger scale
   * @param {number} magnitude - Visual magnitude
   * @returns {number} Scale factor
   */
  magnitudeToScale(magnitude) {
    // Venus can be -4.6, Jupiter -2.9, Mercury -1.9, Mars -2.9, Saturn 0.5
    // Scale from 0.6 (dim) to 1.2 (bright) - smaller overall
    const scale = 0.9 - magnitude * 0.1;
    return Math.max(0.6, Math.min(1.2, scale));
  }

  /**
   * Convert magnitude to point pixel size
   * @param {number} magnitude - Visual magnitude
   * @returns {number} Pixel size
   */
  magnitudeToPixelSize(magnitude) {
    // Scale from 3 (dim) to 8 (bright) - smaller overall
    const size = 5.5 - magnitude * 0.9;
    return Math.max(3, Math.min(8, size));
  }

  /**
   * Generate HTML description for planet
   * @param {Object} planetData - Planet data from PlanetaryPositions
   * @returns {string} HTML description
   */
  generateDescription(planetData) {
    return `
      <h3>${planetData.name}</h3>
      <table>
        <tr><th>Right Ascension</th><td>${planetData.ra.toFixed(4)} hours</td></tr>
        <tr><th>Declination</th><td>${planetData.dec.toFixed(2)}°</td></tr>
        <tr><th>Magnitude</th><td>${planetData.magnitude.toFixed(2)}</td></tr>
        <tr><th>Illumination</th><td>${planetData.illumination.toFixed(1)}%</td></tr>
        <tr><th>Distance</th><td>${planetData.distance_au.toFixed(4)} AU</td></tr>
      </table>
    `;
  }

  /**
   * Create a canvas with a colored circle for planet billboard
   * @param {Array<number>} rgb - RGB color array [r, g, b]
   * @param {number} size - Canvas size in pixels
   * @returns {HTMLCanvasElement} Canvas element
   */
  createPlanetCanvas(rgb, size = 16) {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d");

    // Draw glow
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 1)`);
    gradient.addColorStop(0.5, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.8)`);
    gradient.addColorStop(1, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0)`);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    // Draw bright center
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 4, 0, 2 * Math.PI);
    ctx.fillStyle = `rgba(255, 255, 255, 0.9)`;
    ctx.fill();

    return canvas;
  }

  /**
   * Get current render mode
   * @returns {string} Current mode ('billboard' or 'point')
   */
  getRenderMode() {
    return this.renderMode;
  }

  /**
   * Check if planets are enabled
   * @returns {boolean} True if enabled
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Update component visibility based on enabled components
   * @param {Array<string>} enabledComponents - Array of enabled component names
   */
  updateComponents(enabledComponents) {
    this.showLabels = enabledComponents.includes("Label");

    // Check if planet orbits should be shown
    const shouldShowOrbits = enabledComponents.includes("Planet orbits");
    if (shouldShowOrbits !== this.showOrbits) {
      this.showOrbits = shouldShowOrbits;
      this.updatePlanetOrbitsVisibility();
    }
  }

  /**
   * Update distance-based visibility for planets with minDistance (Uranus/Neptune).
   * Throttled to run every 1 second to avoid performance impact.
   * Earth occlusion is handled by Cesium's native depth testing via frustum rendering order.
   */
  updateDistanceVisibility() {
    if (!this.enabled) {
      return;
    }

    // Throttle to once per second
    const now = Date.now();
    if (now - this.lastDistanceCheck < this.distanceCheckInterval) {
      return;
    }
    this.lastDistanceCheck = now;

    // Re-apply frustum far in case something else reset it
    this._updateFrustumFar();

    const cameraDist = Cartesian3.magnitude(this.viewer.camera.positionWC);

    this.planetary.planets.forEach((planetInfo) => {
      const minDist = planetInfo.minDistance || 0;
      if (minDist === 0) return; // Only process planets with minDistance

      const tooClose = cameraDist < minDist;

      // Compute fade alpha (0→1) over 20% range beyond minDistance
      let fadeAlpha = 1.0;
      if (!tooClose) {
        const fadeEnd = minDist * 1.2;
        fadeAlpha = Math.min((cameraDist - minDist) / (fadeEnd - minDist), 1.0);
      }

      if (this.renderMode === "billboard") {
        const entity = this.planetEntities.find((e) => e.id === `planet-${planetInfo.name}`);
        if (entity && entity.billboard) {
          entity.billboard.show = !tooClose;
          if (!tooClose) {
            entity.billboard.color = Color.WHITE.withAlpha(fadeAlpha);
          }
        }
        if (entity && entity.label) {
          entity.label.show = !tooClose && this.showLabels;
          if (!tooClose) {
            entity.label.fillColor = Color.WHITE.withAlpha(fadeAlpha);
          }
        }
      } else if (this.renderMode === "point" && this.pointPrimitives) {
        const point = this.pointPrimitives._pointPrimitives.find((p) => p.planetName === planetInfo.name);
        if (point) {
          point.show = !tooClose;
          if (!tooClose) {
            const baseColor = Color.fromBytes(planetInfo.color[0], planetInfo.color[1], planetInfo.color[2], Math.round(fadeAlpha * 255));
            point.color = baseColor;
          }
        }
        const labelEntity = this.planetEntities.find((e) => e.id === `planet-label-${planetInfo.name}`);
        if (labelEntity && labelEntity.label) {
          labelEntity.label.show = !tooClose && this.showLabels;
          if (!tooClose) {
            labelEntity.label.fillColor = Color.WHITE.withAlpha(fadeAlpha);
          }
        }
      }
    });
  }

  /**
   * Extend the camera frustum far plane so distant planets aren't clipped.
   * Neptune can be ~4.7 trillion meters from Earth; without this the default
   * far plane culls all planets beyond it.
   */
  _updateFrustumFar() {
    const cameraDistance = Cartesian3.magnitude(this.viewer.camera.positionWC);
    const maxPlanetDistance = 4.7e12; // Neptune at ~30 AU
    const required = cameraDistance + maxPlanetDistance;
    if (required > this.viewer.camera.frustum.far) {
      this.viewer.camera.frustum.far = required;
    }
  }

  /**
   * Get planet position in heliocentric inertial frame (Sun-centered ICRF).
   * Returns pure heliocentric coordinates — the modelMatrix in
   * CelestialOrbitRenderer handles the Earth offset for display.
   * @param {string} planetName - Name of the planet
   * @param {JulianDate} time - Time for position calculation
   * @returns {Cartesian3} Position in ICRF frame relative to Sun
   */
  getPlanetPositionInertial(planetName, time) {
    const jsDate = JulianDate.toDate(time);
    const planet = this.planetary.planets.find((p) => p.name === planetName);
    if (!planet) {
      return new Cartesian3(0, 0, 0);
    }

    // Get heliocentric position (planet relative to Sun) in ICRF frame
    // HelioVector returns position in AU, we need to convert to meters
    const AU_TO_METERS = 1.496e11;
    const helioVector = Astronomy.HelioVector(planet.body, jsDate);

    return new Cartesian3(helioVector.x * AU_TO_METERS, helioVector.y * AU_TO_METERS, helioVector.z * AU_TO_METERS);
  }

  /**
   * Update planet orbit visibility
   * Called when showOrbits state changes
   */
  updatePlanetOrbitsVisibility() {
    if (!this.enabled) {
      return;
    }

    const planets = this.planetary.getPlanetNames();

    if (this.showOrbits) {
      planets.forEach((planetName) => {
        if (!this.orbitRenderer.hasOrbit(planetName)) {
          const orbitalPeriod = this.orbitalPeriods[planetName];

          if (!orbitalPeriod) {
            console.warn(`No orbital period defined for ${planetName}`);
            return;
          }

          // Create position function that returns inertial frame position
          const positionFunction = (time) => {
            return this.getPlanetPositionInertial(planetName, time);
          };

          const planetInfo = this.planetary.planets.find((p) => p.name === planetName);

          // Add orbit with planet-specific color
          this.orbitRenderer.addOrbit(planetName, positionFunction, {
            orbitalPeriod: orbitalPeriod,
            color: Color.MEDIUMPURPLE,
            width: 3, // Thicker to be more visible
            resolution: orbitalPeriod / 200, // 200 samples per orbit for smooth ellipses
            leadTimeFraction: 1.0, // Show full orbit ahead
            trailTimeFraction: 0.0, // Don't show trail behind
            referenceFrame: ReferenceFrame.INERTIAL,
            usePrimitive: true, // Use Primitive + modelMatrix for LOD-culling-free rendering
            heliocentric: true, // Sun-centric orbit — proper ellipses centered on the Sun
            minDistance: planetInfo?.minDistance || 0,
          });
        }
      });
    } else {
      // Remove all planet orbits
      planets.forEach((planetName) => {
        if (this.orbitRenderer.hasOrbit(planetName)) {
          this.orbitRenderer.removeOrbit(planetName);
        }
      });
    }
  }
}
